/**
 * useAdminStats Hook
 *
 * Custom hook for fetching admin dashboard statistics.
 * Calls the getAdminStats callable function to retrieve counts.
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseFunctions } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';

/**
 * Admin dashboard statistics
 */
interface AdminStats {
  /** Number of pending gym requests awaiting review */
  pendingRequests: number;
  /** Total number of gyms in the system */
  totalGyms: number;
  /** Total number of registered users */
  totalUsers: number;
}

/**
 * Response type from getAdminStats callable function
 */
interface AdminStatsResponse {
  /** Whether the request was successful */
  success: boolean;
  /** Dashboard statistics */
  stats: AdminStats;
}

/**
 * State interface for admin stats query
 */
interface UseAdminStatsState {
  /** Admin dashboard statistics */
  stats: AdminStats | null;
  /** Whether data is being loaded */
  loading: boolean;
  /** Error message if query failed */
  error: string | null;
}

/**
 * Return type for useAdminStats hook
 */
interface UseAdminStatsResult extends UseAdminStatsState {
  /** Refetch stats from the callable function */
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch admin dashboard statistics
 *
 * @returns Object containing stats, loading state, error, and refetch function
 *
 * @example
 * ```tsx
 * const { stats, loading, error, refetch } = useAdminStats();
 *
 * if (loading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage message={error} />;
 *
 * return (
 *   <div>
 *     <p>Pending requests: {stats?.pendingRequests}</p>
 *     <p>Total gyms: {stats?.totalGyms}</p>
 *     <p>Total users: {stats?.totalUsers}</p>
 *     <button onClick={refetch}>Refresh</button>
 *   </div>
 * );
 * ```
 */
export const useAdminStats = (): UseAdminStatsResult => {
  const { user, userProfile } = useAuth();

  const [state, setState] = useState<UseAdminStatsState>({
    stats: null,
    loading: true,
    error: null,
  });

  /**
   * Fetch stats from the callable function
   */
  const fetchStats = useCallback(async (): Promise<void> => {
    // Skip if not logged in or not admin
    if (!user || !userProfile?.isAdmin) {
      setState({ stats: null, loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const functions = getFirebaseFunctions();
      const getAdminStats = httpsCallable<void, AdminStatsResponse>(functions, 'getAdminStats');

      const result = await getAdminStats();

      if (result.data.success) {
        setState({ stats: result.data.stats, loading: false, error: null });
      } else {
        setState((prev) => ({ ...prev, loading: false, error: 'Failed to fetch stats' }));
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch admin stats';
      console.error('Error fetching admin stats:', err);
      setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
    }
  }, [user, userProfile?.isAdmin]);

  // Fetch stats when user and admin status are available
  useEffect(() => {
    // Only fetch if we have user profile loaded (not still loading)
    if (userProfile !== null || user === null) {
      fetchStats();
    }
  }, [fetchStats, userProfile, user]);

  return {
    ...state,
    refetch: fetchStats,
  };
};
